// File Drop Section — makes a whole region a drop target, not just the dashed
// FileUploadArea box inside it. Without this, a file dropped anywhere in the
// Submit Content section except precisely on the box has no handler, so the
// browser falls back to its default and opens the file in a new tab instead of
// uploading it (EPD-12704). The inner FileUploadArea keeps its own drop handling
// for the box; a drop it already handled is skipped here via defaultPrevented.
//
// Only file drags are touched: a text or URL dragged into an input inside the
// section (e.g. a post URL into "Link to Live") keeps its default insertion.

import { useCallback } from "react";

/** True for a drag carrying files from the OS, as opposed to text or a link. */
export function isFileDrag(e: { dataTransfer: DataTransfer | null }): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes("Files");
}

interface FileDropSectionProps {
  /** Omit to only stop the browser opening a dropped file, without uploading it. */
  onFilesSelected?: (files: FileList) => void;
  /** Ignores drops, e.g. while a submission is in progress. */
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

export function FileDropSection({
  onFilesSelected,
  disabled = false,
  className,
  children,
}: FileDropSectionProps) {
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Permit a file drop so the browser doesn't open the file; applies to the
    // whole section, including the gaps around the dashed box.
    if (isFileDrag(e)) e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      // The inner FileUploadArea runs first for a drop on the box and calls
      // preventDefault there; don't process the same files twice.
      if (e.defaultPrevented || !isFileDrag(e)) return;
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (!disabled && onFilesSelected && files.length > 0) {
        onFilesSelected(files);
      }
    },
    [disabled, onFilesSelected],
  );

  return (
    <div className={className} onDragOver={handleDragOver} onDrop={handleDrop}>
      {children}
    </div>
  );
}
